/**
 * Google Analytics 事件跟踪工具函数
 * 使用 VueGtag 配置的实例
 */

// 存储 VueGtag 实例
let gtagInstance = null;

// 检查是否为生产环境（避免开发环境污染数据）
const isProduction = () => {
  const host = window.location.host;
  return host.indexOf('127.0.0.1') === -1 && 
         host.indexOf('localhost') === -1 && 
         host.indexOf('192.168.') === -1;
};

/**
 * 初始化 analytics 工具，设置 VueGtag 实例
 * @param {Object} gtag - VueGtag 实例 ($gtag)
 */
export const initAnalytics = (gtag) => {
  if (gtag && typeof gtag === 'function') {
    gtagInstance = gtag;
    console.log('Analytics initialized successfully with VueGtag instance');
  } else {
    console.warn('Invalid gtag instance provided to initAnalytics');
  }
};

/**
 * 检查 Analytics 是否已正确初始化
 * @returns {boolean} 是否已初始化
 */
export const isAnalyticsReady = () => {
  return gtagInstance !== null && typeof gtagInstance === 'function';
};

/**
 * 获取当前的 gtag 实例
 * 优先使用 VueGtag 实例，回退到全局 gtag
 */
const getGtagInstance = () => {
  // 优先使用 VueGtag 实例，并严格检查是否为函数
  if (gtagInstance && typeof gtagInstance === 'function') {
    return gtagInstance;
  }
  
  // 尝试从全局作用域获取 gtag
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    return window.gtag;
  }
  
  // 回退到全局 gtag（兼容性）
  if (typeof gtag !== 'undefined' && typeof gtag === 'function') {
    return gtag;
  }
  
  return null;
};

/**
 * 发送自定义事件到Google Analytics
 * @param {string} eventName - 事件名称
 * @param {Object} parameters - 事件参数
 */
export const trackEvent = (eventName, parameters = {}) => {
  try {
    const gtagFunc = getGtagInstance();
    if (gtagFunc && typeof gtagFunc === 'function' && isProduction()) {
      gtagFunc('event', eventName, {
        event_category: parameters.category || 'engagement',
        event_label: parameters.label || '',
        value: parameters.value || 0,
        custom_parameter: parameters.custom || '',
        ...parameters
      });
    }
  } catch (error) {
    // 静默处理错误，避免影响用户体验
    console.warn('Analytics trackEvent failed:', error);
  }
};

/**
 * 跟踪页面浏览
 * @param {string} pagePath - 页面路径
 * @param {string} pageTitle - 页面标题
 */
export const trackPageView = (pagePath, pageTitle) => {
  try {
    const gtagFunc = getGtagInstance();
    if (gtagFunc && typeof gtagFunc === 'function' && isProduction()) {
      gtagFunc('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle,
        page_location: window.location.href
      });
    }
  } catch (error) {
    // 静默处理错误，避免影响用户体验
    console.warn('Analytics trackPageView failed:', error);
  }
};

/**
 * 跟踪用户登录
 * @param {string} method - 登录方式
 */
export const trackLogin = (method = 'unknown') => {
  trackEvent('login', {
    category: 'user_engagement',
    label: method
  });
};

/**
 * 跟踪用户注册
 * @param {string} method - 注册方式
 */
export const trackSignUp = (method = 'unknown') => {
  trackEvent('sign_up', {
    category: 'user_engagement',
    label: method
  });
};

/**
 * 跟踪按钮点击
 * @param {string} buttonName - 按钮名称
 * @param {string} location - 按钮位置
 */
export const trackButtonClick = (buttonName, location = '') => {
  trackEvent('button_click', {
    category: 'user_interaction',
    label: `${buttonName}${location ? '_' + location : ''}`
  });
};

/**
 * 跟踪表单提交
 * @param {string} formName - 表单名称
 * @param {boolean} success - 是否成功
 */
export const trackFormSubmit = (formName, success = true) => {
  trackEvent('form_submit', {
    category: 'form_interaction',
    label: formName,
    value: success ? 1 : 0
  });
};

/**
 * 跟踪文件下载
 * @param {string} fileName - 文件名
 * @param {string} fileType - 文件类型
 */
export const trackDownload = (fileName, fileType = '') => {
  trackEvent('file_download', {
    category: 'engagement',
    label: fileName,
    custom_parameter: fileType
  });
};

/**
 * 跟踪搜索行为
 * @param {string} searchTerm - 搜索词
 * @param {number} resultCount - 结果数量
 */
export const trackSearch = (searchTerm, resultCount = 0) => {
  trackEvent('search', {
    category: 'engagement',
    label: searchTerm,
    value: resultCount
  });
};

/**
 * 跟踪面试相关事件（针对您的面试系统）
 */
export const trackInterview = {
  // 开始面试
  start: (interviewType = 'unknown') => {
    trackEvent('interview_start', {
      category: 'interview',
      label: interviewType
    });
  },
  
  // 完成面试
  complete: (interviewType = 'unknown', duration = 0) => {
    trackEvent('interview_complete', {
      category: 'interview',
      label: interviewType,
      value: duration // 面试时长（分钟）
    });
  },
  
  // 面试暂停
  pause: (interviewType = 'unknown') => {
    trackEvent('interview_pause', {
      category: 'interview',
      label: interviewType
    });
  },
  
  // 面试恢复
  resume: (interviewType = 'unknown') => {
    trackEvent('interview_resume', {
      category: 'interview',
      label: interviewType
    });
  },
  
  // 提交答案
  submitAnswer: (questionType = 'unknown') => {
    trackEvent('interview_answer_submit', {
      category: 'interview',
      label: questionType
    });
  }
};

// 默认导出所有函数
export default {
  initAnalytics,
  isAnalyticsReady,
  trackEvent,
  trackPageView,
  trackLogin,
  trackSignUp,
  trackButtonClick,
  trackFormSubmit,
  trackDownload,
  trackSearch,
  trackInterview
};
